<template>
	<div class="text-center" :class="classList">
		<c-auth-page-header
			:title="header"
			class="pb-3 light"
		/>
		<div v-if="step === 1">
			<div class="center pb-3">
				<label class="cog-label flex--center" for="job">
					What brings you here?
				</label>
			</div>

			<radio-group
				v-model="selectedJob"
				class="create-industry-organization__occupation flex gap-3 pb-4"
			>
				<radio-button
					v-for="job in jobs"
					:key="job.value"
					class="p-2 pointer"
					:label="job.value"
				>
					<inline :svg="`complex-icons/${job.icon}`" class="w-66" />
					<div class="sz-2">{{ job.value }}</div>
				</radio-button>
			</radio-group>

			<collapse v-model="selectedJob">
				<collapse-item name="School" :disabled="true">
					<div class="pb-1">
						<label class="cog-label flex--center" for="role">
							What is your role?
						</label>
					</div>

					<c-radio-button-bar
						:display-value="selectedRole"
						class="create-industry-organization__school-radio-button-bar sz-3 pb-4 inline-block"
						:options="roles"
						@change="selectedRole = $event"
					>
					</c-radio-button-bar>
				</collapse-item>
				<collapse-item name="Work" :disabled="true">
					<div class="pb-3">
						<label class="cog-label flex--center" for="industry">
							What is your industry?
						</label>
					</div>

					<c-deprecated-select
						id="industry"
						v-model="selectedIndustry"
						class="pb-4"
						:options="workIndustries"
						popper-class="create-industry-organization__industry"
						filterable
					/>
				</collapse-item>
			</collapse>

			<div class="pb-3">
				<label class="cog-label flex--center gap-2" for="organization-name">
					What is your organization's name?
				</label>
			</div>

			<c-deprecated-validated-input
				id="organization-name"
				v-model.trim="organizationName"
				type="text"
				name="Organization Name"
				rules="required|string-length:0,170"
				class="pb-4"
			/>

			<c-status-button
				class="button button--primary bg-deep sz-4 mt-2"
				title="Create Organization" type="submit" :status="continueButtonStatus"
				:disabled="shouldDisableContinue"
				@click.native="createOrg"
			>
				Continue
			</c-status-button>
		</div>
		<div v-else-if="showTemplates">
			<c-select-templates
				:site-manifest="siteManifest"
				:selected-industry="selectedIndustry"
				@done="routeToRedirect"
			/>
		</div>
	</div>
</template>

<script lang="ts">
	import _Vue from 'vue';
	import type { VueConstructor } from 'vue';
	import type { StoreInjected } from 'src/injections';
	import CAuthPageHeader from 'src/components/auth/AuthPageHeader.vue';
	import CRadioButtonBar from '@cognitoforms/shared-components/src/components/form-controls/RadioButtonBar.vue';
	import CDeprecatedValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/DeprecatedValidatedInput.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CDeprecatedSelect from '@cognitoforms/shared-components/src/components/form-controls/DeprecatedSelect.vue';
	import { signupCreateOrg } from '@cognitoforms/api/services/authentication';
	import CSelectTemplates from 'src/views/SelectTemplates.vue';

	import { RadioGroup, RadioButton, Collapse, CollapseItem } from '@cognitoforms/element-ui';
	_Vue.use(RadioGroup);
	_Vue.use(RadioButton);

	const Vue = _Vue as VueConstructor<_Vue & StoreInjected>;

	export default Vue.extend({
		name: 'CreateOrganization',
		components: {
			CAuthPageHeader,
			CDeprecatedValidatedInput,
			CRadioButtonBar,
			CStatusButton,
			CDeprecatedSelect,
			CSelectTemplates,
			RadioGroup,
			RadioButton,
			Collapse,
			CollapseItem
		},
		inject: ['$store'],
		props: {
			siteManifest: {
				type: Object,
				default: null
			}
		},
		data() {
			return {
				organizationName: '',
				selectedJob: 'Work',
				selectedRole: 'teacher',
				selectedIndustry: null,
				jobs: [
					{
						value: 'Work',
						icon: 'work'
					},
					{
						value: 'School',
						icon: 'school'
					},
					{
						value: 'Personal',
						icon: 'account-organization'
					}
				],
				roles: [
					{
						displayValue: 'Teacher/Admin',
						value: 'teacher'
					},
					{
						displayValue: 'Student',
						value: 'student'
					}
				],
				industries: [],
				continueButtonStatus: 'default',
				redirectRoute: null,
				step: 1
			};
		},
		head() {
			return {
				title: 'Create Organization',
				meta: [
					{
						name: 'description',
						content: 'Cognito Forms, a free online form builder that helps you collect information and payments. Easily create feedback forms, payment forms, registration forms, and much more.'
					}
				]
			};
		},
		computed: {
			header() {
				if (this.step === 1)
					return 'Welcome, ' + this.$store.profile.firstName + '!';
				return 'Select some templates to get started.';
			},
			classList() {
				if (this.step === 2)
					return 'select-templates';
				return 'create-industry-organization';
			},
			isTeacher() {
				return this.selectedJob === 'School' && this.selectedRole === 'teacher';
			},
			isStudent() {
				return this.selectedJob === 'School' && this.selectedRole === 'student';
			},
			isPersonal() {
				return this.selectedJob === 'Personal';
			},
			shouldDisableContinue() {
				return !this.organizationName || (!this.selectedIndustry && !(this.isTeacher || this.isStudent || this.isPersonal));
			},
			showTemplates() {
				return this.step === 2 && !this.isPersonal && !this.isStudent && !this.fromFormGeneration;
			},
			fromFormGeneration() {
				return this.redirectRoute?.includes('/l/ai-form-generation');
			},
			workIndustries() {
				return this.industries.filter(industry => industry.displayValue !== 'Personal');
			}
		},
		watch: {
			selectedJob() {
				if (this.isPersonal && !this.organizationName) {
					// Default organization name to user name if empty
					this.organizationName = this.$store.profile.lastName ? this.$store.profile.name : this.$store.profile.firstName;
				}
			},
			selectedRole() {
				if (this.isStudent && !this.organizationName) {
					// Default organization name to user name if empty
					this.organizationName = this.$store.profile.lastName ? this.$store.profile.name : this.$store.profile.firstName;
				}
			}
		},
		mounted() {
			this.$store.loadProfile();

			if (this.siteManifest) {
				this.industries = this.siteManifest.industries.list.all.map(industry => {
					return {
						displayValue: industry.title,
						value: industry.cfid
					};
				});
			}
		},
		methods: {
			createOrg() {
				this.continueButtonStatus = 'validating';

				// If school teacher, set industry category
				if (this.isTeacher) {
					this.selectedIndustry = this.industries.find(i => i.value === '1')?.value; // K-12 Education
				}
				// If personal or student, set industry category
				else if (this.isPersonal || this.isStudent) {
					this.selectedIndustry = this.industries.find(i => i.value === '35')?.value;
				}

				signupCreateOrg(this.organizationName, this.selectedIndustry, this.industries.find(i => i.value === this.selectedIndustry)?.displayValue)
					.then(res => {
						if (res.Succeeded) {
							this.redirectRoute = res.ReturnUrl;
						}

						// If industry is personal, redirect to return url and skip templates page
						const hasPersonalIndustry = this.industries.find(i => i.value === this.selectedIndustry)?.displayValue === 'Personal';
						if (hasPersonalIndustry)
							return window.location.assign(this.redirectRoute);

						// If signing up from a template, use return url to copy and skip templates page
						if (this.redirectRoute.includes('source=')) {
							const returnUrl = new URL(this.redirectRoute);
							const searchParams = returnUrl.search.split('&');
							const source = searchParams.filter(p => p.includes('source='))[0];
							if (this.fromFormGeneration || source.includes('template') || source.includes('jotform'))
								return window.location.assign(this.redirectRoute);
						}
						// Otherwise, continue to templates page for selected industry
						this.step = 2;

						this.continueButtonStatus = 'default';
					})
					.catch(e => {
						console.warn(e);
						this.continueButtonStatus = 'error';
					});
			},
			routeToRedirect() {
				return window.location.assign(this.redirectRoute);
			}
		}
	});
</script>

<style lang="scss">
// Do not use below font elsewhere in the app.
@font-face {
	font-family: Gilroy;
	font-style: normal;
	font-weight: 300;
	src:
		url(https://static.cognitoforms.com/fonts/gilroy/gilroy-light_basic-latin.woff2) format('woff2'),
		url(https://static.cognitoforms.com/fonts/gilroy/gilroy-light_basic-latin.woff) format('woff');
	font-display: swap;
}

.create-industry-organization {
	// TODO: Add gap class to utility classes
	// Do after AMP is killed since it will increase amp css slightly and probably cause erros
	.gap-2 {
		gap: $gutter * .25;
	}

	.gap-3 {
		gap: $gutter * .5;
	}

	&__occupation {

		input {
			display: none;
		}

		label {
			min-height: 96px;
			border: 1px solid $lange;
		}

		.is-active {
			background: $alexander;
			border-color: $benjamin;

			svg [fill] {
				fill: $abraham;
			}
		}

		label:not(.is-active):hover {
			background: $bg__hovered;
			transition: background-color 1s;

			svg [fill] {
				transition: background-color .4s;
				fill: $thetis;
			}
		}
	}

	&__school-radio-button-bar .el-radio-button__inner {
		// Tab bar is slightly taller than input. Shave off a pixel here to prevent a jitter.
		padding-top: 9px;
	}
}

.el-collapse-item {

	&__wrap {
		overflow: hidden;
		will-change: height;
		transition: height .25s;
	}
}

.select-templates {
	align-content: center;
}

[data-auth-style='app'] .social-login {

	.faux-modal__body {
		@include contain-until($b5);
		padding: $space__5;
		padding-bottom: 50px;
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	h1 {
		font-size: $size__7;
		font-weight: 300;
	}

	.el-input__inner {
		min-height: 40px;
		padding-left: 1em;
	}

	.button--primary {
		width: 100%;
	}

	.cog-label {
		margin-bottom: 0;
		font-size: $size__3;
		font-weight: $weight__text--normal;
	}

	.template-selection__grid {
		margin-bottom: $space__5;
	}
}
</style>